/**
 * Pure selectors over the Bot Ops snapshot: status order, the Action Center row
 * order, KPI counts, C-suite pills, bot-table sorting, hierarchy and Grant-asks
 * grouping. No React, no fetching, so each rule in docs/bot-ops-handoff
 * DASHBOARD.md §1 is testable on its own.
 */
import { ActionItem, Bot, Company, ExecCategory, Instruction, LedgerRow, Review, Snapshot, StatusColor, EXEC_ORDER } from './types';
import { TONE } from './brand';
import { isWaiting } from './decideRank';

// Green / yellow / red are fixed by Grant's spec (§1.1), not mapped onto the theme.
export const STATUS_COLORS: Record<StatusColor, string> = { green: '#16a34a', yellow: '#eab308', red: '#dc2626' };
export const STATUS_LABEL: Record<StatusColor, string> = { green: 'Active', yellow: 'Pending decision', red: 'Idle' };

/** Yellow first (needs Grant), then green, then red. */
export const statusRank = (c: StatusColor): number => ({ yellow: 0, green: 1, red: 2 }[c] ?? 3);

export const COMPANIES: Company[] = ['lifescribe', 'quill'];
export const isCompany = (v: string | null | undefined): v is Company => v === 'lifescribe' || v === 'quill';

export const botsOf = (s: Snapshot, company: Company): Bot[] => s.bots.filter((b) => b.company === company);

export const botBySlug = (s: Snapshot, slug: string | null | undefined): Bot | undefined =>
  slug ? s.bots.find((b) => b.slug === slug) : undefined;

// ---- Action Center -------------------------------------------------------

export interface ActionRow {
  action: ActionItem;
  bot: Bot;
}

/** §1.4 row order: Decision, High, Medium, Reviewed, Next step + Blocker, Low, Unscored. */
export const priorityGroup = (p: string): number => {
  switch (p) {
    case 'Decision': return 0;
    case 'High': return 1;
    case 'Medium': return 2;
    case 'Reviewed': return 3;
    case 'Next step':
    case 'Blocker': return 4;
    case 'Low': return 5;
    default: return 6;
  }
};

const scoreOf = (a: ActionItem): number => (typeof a.score === 'number' ? a.score : -1);

/** Group first; within Decision, exec alternatives before agrees; then score, high to low. */
export function sortRows(rows: ActionRow[]): ActionRow[] {
  return rows.slice().sort((x, y) => {
    const g = priorityGroup(x.action.priority) - priorityGroup(y.action.priority);
    if (g) return g;
    if (x.action.priority === 'Decision') {
      const ax = x.action.exec_review === 'ALTERNATIVE' ? 0 : 1;
      const ay = y.action.exec_review === 'ALTERNATIVE' ? 0 : 1;
      if (ax !== ay) return ax - ay;
    }
    const s = scoreOf(y.action) - scoreOf(x.action);
    if (s) return s;
    return x.action.id < y.action.id ? -1 : x.action.id > y.action.id ? 1 : 0;
  });
}

/**
 * The Action Center rows. With a bot selected: that bot's items, whichever
 * company it is in (Quill shows LifeScribe's execs as its C-suite, and clicking
 * one must show that exec's items). Otherwise: every item of the company's bots.
 */
export function actionRows(s: Snapshot, opts: { company: Company; bot?: string | null }): ActionRow[] {
  const pool = opts.bot ? s.bots.filter((b) => b.slug === opts.bot) : botsOf(s, opts.company);
  const rows: ActionRow[] = [];
  pool.forEach((bot) => (bot.actions || []).forEach((action) => rows.push({ action, bot })));
  return sortRows(rows);
}

export type DecisionFilter = 'all' | 'pending' | 'approved' | 'declined' | 'instructed' | 'blockers';
export const DECISION_FILTERS: DecisionFilter[] = ['pending', 'approved', 'declined', 'instructed', 'blockers', 'all'];
/** The Action Center opens on what is waiting for Grant, not on every row. */
export const DEFAULT_DECISION: DecisionFilter = 'pending';

const decisionBucket = (a: ActionItem): Exclude<DecisionFilter, 'all' | 'blockers'> => {
  switch (a.decision && a.decision.value) {
    case 'yes': return 'approved';
    case 'no': return 'declined';
    case 'instructed': return 'instructed';
    default: return 'pending';
  }
};

export function decisionCounts(rows: ActionRow[]): Record<DecisionFilter, number> {
  const c: Record<DecisionFilter, number> = { all: rows.length, pending: 0, approved: 0, declined: 0, instructed: 0, blockers: 0 };
  rows.forEach(({ action }) => {
    // A blocker is not a proposal: it is never "pending a decision"; it has its own pill.
    if (!action.decidable) c.blockers += 1;
    if (!action.decidable && decisionBucket(action) === 'pending') return;
    c[decisionBucket(action)] += 1;
  });
  return c;
}

export function filterByDecision(rows: ActionRow[], f: DecisionFilter): ActionRow[] {
  if (f === 'all') return rows;
  if (f === 'blockers') return rows.filter(({ action }) => !action.decidable);
  return rows.filter(({ action }) => {
    if (f === 'pending' && !action.decidable) return false;
    return decisionBucket(action) === f;
  });
}

// ---- Action Center team filters (C-suite line and org-chart area) ---------

/** The bots an exec answers for: its reporting/review lines (exec_categories) plus the exec's own bot. */
export function execSlugs(s: Snapshot, exec: string): Set<string> {
  const cat: ExecCategory | undefined = s.exec_categories && s.exec_categories[exec];
  const set = new Set<string>(cat ? cat.slugs : []);
  s.bots.forEach((b) => { if (b.tier === 'exec' && b.exec_label === exec) set.add(b.slug); });
  return set;
}

export const COORDINATOR_AREA = 'Coordinator';
export const CSUITE_AREA = 'C-suite';

/** Which org-chart area a bot sits in: its cluster, or the coordinator / C-suite tier. */
export const areaOf = (b: Bot): string => (b.tier === 'bot' ? b.group : b.tier === 'coordinator' ? COORDINATOR_AREA : CSUITE_AREA);

/** Area pills in org-chart order (the company's clusters); the coordinator and C-suite only when they own items. */
export function areaNames(s: Snapshot, company: Company, rows: ActionRow[]): string[] {
  const clusters = hierarchy(s, company).clusters.map((c) => c.name);
  const extra = [COORDINATOR_AREA, CSUITE_AREA].filter((a) => rows.some((r) => areaOf(r.bot) === a));
  return [...clusters, ...extra];
}

export const filterByTeam = (rows: ActionRow[], s: Snapshot, exec: string | null): ActionRow[] => {
  if (!exec) return rows;
  const set = execSlugs(s, exec);
  return rows.filter((r) => set.has(r.bot.slug));
};

export const filterByArea = (rows: ActionRow[], area: string | null): ActionRow[] =>
  (area ? rows.filter((r) => areaOf(r.bot) === area) : rows);

export function teamCounts(rows: ActionRow[], s: Snapshot): Record<string, number> {
  const out: Record<string, number> = {};
  EXEC_ORDER.forEach((e) => { const set = execSlugs(s, e); out[e] = rows.filter((r) => set.has(r.bot.slug)).length; });
  return out;
}

export function areaCounts(rows: ActionRow[], names: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  names.forEach((n) => { out[n] = 0; });
  rows.forEach((r) => { const a = areaOf(r.bot); if (a in out) out[a] += 1; });
  return out;
}

// ---- Action Center: bucket and implementation filters, and all five together ---

export const BUCKETS = ['Subscription', 'Security', 'Everything else', 'Unbucketed'] as const;
export type Bucket = (typeof BUCKETS)[number];
export const bucketOf = (a: ActionItem): Bucket =>
  (a.bucket === 'Subscription' || a.bucket === 'Security' || a.bucket === 'Everything else' ? a.bucket : 'Unbucketed');
export const isBucket = (v: string | null | undefined): v is Bucket => Boolean(v) && (BUCKETS as readonly string[]).includes(v as string);

export const IMPL_FILTERS = ['queued', 'in_progress', 'done', 'blocked', 'failed'] as const;
export type ImplFilter = (typeof IMPL_FILTERS)[number];
export const IMPL_LABEL: Record<ImplFilter, string> = { queued: 'Queued', in_progress: 'In progress', done: 'Implemented', blocked: 'Blocked', failed: 'Failed' };

export function bucketCounts(rows: ActionRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  BUCKETS.forEach((b) => { out[b] = 0; });
  rows.forEach((r) => { out[bucketOf(r.action)] += 1; });
  return out;
}

export interface AcFilters { decision: DecisionFilter; team: string | null; area: string | null; bucket: Bucket | null; impl: ImplFilter | null }

/** Every Action Center filter, combined with AND; `skip` leaves one out (for that pill row's own counts). */
export function applyAcFilters(rows: ActionRow[], s: Snapshot, f: AcFilters, skip?: keyof AcFilters): ActionRow[] {
  let out = rows;
  if (skip !== 'team') out = filterByTeam(out, s, f.team);
  if (skip !== 'area') out = filterByArea(out, f.area);
  if (skip !== 'bucket' && f.bucket) out = out.filter((r) => bucketOf(r.action) === f.bucket);
  if (skip !== 'impl' && f.impl) out = out.filter((r) => (r.action.implementation && r.action.implementation.status) === f.impl);
  if (skip !== 'decision') out = filterByDecision(out, f.decision);
  return out;
}

export const findAction = (s: Snapshot, id: string | null | undefined): ActionRow | null => {
  if (!id) return null;
  for (const bot of s.bots) {
    const action = (bot.actions || []).find((a) => a.id === id);
    if (action) return { action, bot };
  }
  return null;
};

// ---- Summary strip -------------------------------------------------------

const isOpenRow = (r: LedgerRow): boolean => !r.struck && /^OPEN/i.test(r.status || '');

export interface Kpis {
  total: number;
  active: number;
  pending: number;
  idle: number;
  openActions: number;
  awaiting: number;
  alternatives: number;
  gaOpen: number;
  secOpen: number;
}

export function kpis(s: Snapshot, company: Company): Kpis {
  const bots = botsOf(s, company);
  const n = (c: StatusColor) => bots.filter((b) => b.color === c).length;
  const acts: ActionItem[] = [];
  bots.forEach((b) => (b.actions || []).forEach((a) => acts.push(a)));
  const waiting = acts.filter(isWaiting);
  return {
    total: bots.length,
    active: n('green'),
    pending: n('yellow'),
    idle: n('red'),
    // Open = not declined and not implemented yet (pending, instructed, approved and under way, blockers).
    openActions: acts.filter((a) => !(a.decision && a.decision.value === 'no') && !(a.implementation && a.implementation.status === 'done')).length,
    // Awaiting Grant = exactly what the Decide today queue counts: decidable and not yet decided.
    awaiting: waiting.length,
    alternatives: waiting.filter((a) => a.exec_review === 'ALTERNATIVE' || a.recommended_by === 'exec').length,
    // The Open GA tile counts both companies (it is the same ledger).
    gaOpen: (s.ledger || []).filter((r) => /^GA-/.test(r.id) && isOpenRow(r)).length,
    secOpen: (s.ledger || []).filter((r) => /^SEC-/.test(r.id) && isOpenRow(r)).length,
  };
}

// ---- Hierarchy ------------------------------------------------------------

export interface Hierarchy {
  execs: Bot[];
  coordinator: Bot | null;
  clusters: { name: string; bots: Bot[] }[];
}

/** C-suite, the company's coordinator, then clusters in the order the snapshot lists them. */
export function hierarchy(s: Snapshot, company: Company): Hierarchy {
  const mine = botsOf(s, company);
  const order: string[] = [];
  const by = new Map<string, Bot[]>();
  mine.filter((b) => b.tier === 'bot').forEach((b) => {
    if (!by.has(b.group)) { by.set(b.group, []); order.push(b.group); }
    (by.get(b.group) as Bot[]).push(b);
  });
  return {
    // LifeScribe's execs also lead Quill (TEAM-BRIEF), so both companies show them.
    execs: EXEC_ORDER.map((label) => s.bots.find((b) => b.tier === 'exec' && b.exec_label === label)).filter((b): b is Bot => Boolean(b)),
    coordinator: mine.find((b) => b.tier === 'coordinator') || null,
    clusters: order.map((name) => ({ name, bots: by.get(name) as Bot[] })),
  };
}

// ---- Bot table -------------------------------------------------------------

export type SortKey = 'bot' | 'status' | 'doing' | 'finding' | 'actions' | 'verdict' | 'updated';
export const SORT_KEYS: SortKey[] = ['bot', 'status', 'doing', 'finding', 'actions', 'verdict', 'updated'];
export type SortDir = 'asc' | 'desc';

export type StatusFilter = StatusColor | null;

export function execCounts(s: Snapshot, company: Company): { all: number; byExec: Record<string, number> } {
  const slugs = new Set(botsOf(s, company).map((b) => b.slug));
  const byExec: Record<string, number> = {};
  EXEC_ORDER.forEach((e) => {
    const cat: ExecCategory | undefined = s.exec_categories && s.exec_categories[e];
    byExec[e] = cat ? cat.slugs.filter((x) => slugs.has(x)).length : 0;
  });
  return { all: slugs.size, byExec };
}

export function tableBots(s: Snapshot, company: Company, f: { status?: StatusFilter; exec?: string | null }): Bot[] {
  let list = botsOf(s, company);
  if (f.status) list = list.filter((b) => b.color === f.status);
  if (f.exec) {
    const cat = s.exec_categories && s.exec_categories[f.exec];
    const set = new Set(cat ? cat.slugs : []);
    list = list.filter((b) => set.has(b.slug));
  }
  return list;
}

const topScore = (b: Bot): number => (b.findings && b.findings.length && typeof b.findings[0].score === 'number' ? (b.findings[0].score as number) : -1);
const time = (iso?: string | null): number => (iso ? new Date(iso).getTime() || 0 : 0);

export function sortBots(bots: Bot[], key: SortKey, dir: SortDir): Bot[] {
  const sign = dir === 'asc' ? 1 : -1;
  const cmp = (a: Bot, b: Bot): number => {
    switch (key) {
      case 'bot': return a.name.localeCompare(b.name);
      case 'status': {
        const r = statusRank(a.color) - statusRank(b.color);
        if (r) return r;
        // Within a colour, execs first, then the rest by name.
        const t = (a.tier === 'exec' ? 0 : 1) - (b.tier === 'exec' ? 0 : 1);
        return t || a.name.localeCompare(b.name);
      }
      case 'doing': return String((a.doing && a.doing.text) || '').localeCompare(String((b.doing && b.doing.text) || ''));
      case 'finding': return topScore(a) - topScore(b);
      case 'actions': return (a.actions || []).length - (b.actions || []).length;
      case 'verdict': return (a.reviews || []).length - (b.reviews || []).length;
      case 'updated': return time(a.last_activity_ict) - time(b.last_activity_ict);
      default: return 0;
    }
  };
  return bots.slice().sort((a, b) => sign * cmp(a, b) || a.name.localeCompare(b.name));
}

// ---- Verdict chips ----------------------------------------------------------

export interface VerdictChip { exec: string; verdict: Review['verdict'] }

/** Distinct (exec, verdict) pairs for a bot, alternatives first. */
export function verdictChips(bot: Bot): VerdictChip[] {
  const seen = new Set<string>();
  const out: VerdictChip[] = [];
  (bot.reviews || []).forEach((r) => {
    const k = `${r.exec}|${r.verdict}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ exec: r.exec, verdict: r.verdict });
  });
  return out.sort((a, b) => (a.verdict === b.verdict ? 0 : a.verdict === 'alternative' ? -1 : 1));
}

// ---- Grant asks (MASTER-LEDGER) ---------------------------------------------

export interface LedgerSections { critical: LedgerRow[]; asks: LedgerRow[]; runsheet: LedgerRow[] }

export function ledgerSections(ledger: LedgerRow[]): LedgerSections {
  return {
    critical: ledger.filter((r) => /^SEC-/.test(r.id)),
    asks: ledger.filter((r) => /^GA-/.test(r.id)),
    runsheet: ledger.filter((r) => /^RUN-/.test(r.id)),
  };
}

/** Ledger id (GA-21, SEC-001) -> the Action Center items that cite it. */
export function gaIndex(s: Snapshot): Map<string, ActionRow[]> {
  const map = new Map<string, ActionRow[]>();
  s.bots.forEach((bot) => (bot.actions || []).forEach((action) => {
    const ids = new Set<string>();
    ((action.review && action.review.ga) || []).forEach((g) => ids.add(g.id));
    ((action.why && action.why.linked_ids) || []).forEach((id) => { if (/^(GA|SEC)-/.test(id)) ids.add(id); });
    ids.forEach((id) => {
      if (!map.has(id)) map.set(id, []);
      (map.get(id) as ActionRow[]).push({ action, bot });
    });
  }));
  return map;
}

// ---- Priority chip styles (§1.12) ---------------------------------------------

export const PRIORITY_STYLE: Record<string, { bg: string; fg: string; border?: string }> = {
  Decision: { bg: TONE.warn.bg, fg: TONE.warn.fg, border: TONE.warn.border },
  High: { bg: TONE.bad.bg, fg: TONE.bad.fg },
  Medium: { bg: TONE.warn.bg, fg: TONE.warn.fg },
  Reviewed: { bg: TONE.good.bg, fg: TONE.good.fg },
  'Next step': { bg: TONE.info.bg, fg: TONE.info.fg },
  Blocker: { bg: TONE.strong.bg, fg: TONE.strong.fg },
  Low: { bg: TONE.neutral.bg, fg: TONE.neutral.fg },
  Unscored: { bg: TONE.neutral.bg, fg: TONE.neutral.fg },
};

// ---- Instructions (two-way threads) -----------------------------------------

const instructionsOf = (s: Snapshot): Instruction[] => (Array.isArray(s.instructions) ? s.instructions : []);

/** Newest first. */
const byNewest = (a: Instruction, b: Instruction) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0);

export const instructionsForBot = (s: Snapshot, slug: string): Instruction[] =>
  instructionsOf(s).filter((i) => i.target_bot_slug === slug).sort(byNewest);

export const instructionsForAction = (s: Snapshot, actionId: string): Instruction[] =>
  instructionsOf(s).filter((i) => i.related_action_id === actionId).sort(byNewest);

export const instructionById = (s: Snapshot, id: string | null | undefined): Instruction | undefined =>
  id ? instructionsOf(s).find((i) => i.id === id) : undefined;

/** Unread threads per bot slug (a bot answered since Grant last opened the thread). */
export function unreadByBot(s: Snapshot): Record<string, number> {
  const out: Record<string, number> = {};
  instructionsOf(s).forEach((i) => { if (i.unread) out[i.target_bot_slug] = (out[i.target_bot_slug] || 0) + 1; });
  return out;
}

export const NO_ACK_NUDGE_AFTER_MS = 60 * 1000;
export const NO_ACK_ALERT_AFTER_MS = 24 * 60 * 60 * 1000;

export type ThreadStage = 'sent' | 'delivered' | 'acked';

export interface ThreadState {
  stage: ThreadStage;
  /** No delivered ack within a minute: offer "Resend nudge". */
  notDelivered: boolean;
  /** No bot ack after a day: show the "No bot ack in 24 h" chip. */
  noAck24h: boolean;
  /** The webhook gave up (4 retries): say so, the message itself is safe in S3. */
  webhookFailed: boolean;
}

/** ⏳ Sent → ✓ Delivered → ✓ <bot> acknowledged, and the two "nothing is happening" warnings. */
export function threadState(i: Instruction, now: number): ThreadState {
  const acked = i.bot_ack && i.bot_ack.status === 'acked';
  const delivered = Boolean(i.transport && i.transport.delivered_at) || acked;
  const age = now - new Date(i.created_at).getTime();
  return {
    stage: acked ? 'acked' : delivered ? 'delivered' : 'sent',
    notDelivered: !delivered && age >= NO_ACK_NUDGE_AFTER_MS,
    noAck24h: !acked && age >= NO_ACK_ALERT_AFTER_MS,
    webhookFailed: Boolean(i.transport && i.transport.webhook_status === 'failed'),
  };
}

export const REPLY_STATUS_LABEL: Record<string, string> = {
  in_progress: 'In progress', done: 'Done', blocked: 'Blocked', failed: 'Failed', declined_scope: 'Declined (out of scope)', queued: 'Queued',
};

export const INSTRUCTION_STATUS_LABEL: Record<string, string> = { open: 'Open', in_progress: 'In progress', done: 'Done', declined: 'Declined' };

/** Where "Instruct instead" can go: the owner bot, or the Implementation Bot when the snapshot names one. */
export function instructTargets(s: Snapshot, owner: Bot): { slug: string; label: string }[] {
  const out = [{ slug: owner.slug, label: `${owner.name} (owner)` }];
  const impl = s.implementation_bot;
  if (impl && impl.slug && impl.slug !== owner.slug) out.push({ slug: impl.slug, label: 'Implementation Bot' });
  return out;
}
